import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

import Button from "../../../../../components/buttons/Button";
import TextField from "../../../../../components/input/textfield/TextField";
import TextArea from "../../../../../components/input/textarea/TextArea";
import Select from "../../../../../components/input/select/Select";

import { useCreatorVideos } from "../../hooks/useCreatorVideos";

import {
  getSetPriceAmount,
  validateVideoPriceAmount,
} from "./utils/helper/videoPricing";

import "./CreatorVideoEditForm.css";

const VISIBILITY_OPTIONS = [
  { label: "Public", value: "public" },
  { label: "Unlisted", value: "unlisted" },
  { label: "Private", value: "private" },
];

const SPORT_OPTIONS = [
  { label: "Select sport", value: "" },
  { label: "Rugby Union", value: "Rugby Union" },
  { label: "Rugby League", value: "Rugby League" },
  { label: "Other", value: "Other" },
];

const getErrorMessage = (error, fallback) =>
  typeof error === "string"
    ? error
    : error?.response?.data?.message || error?.message || fallback;

const formatDuration = (seconds) => {
  const total = Math.floor(Number(seconds) || 0);

  return `${Math.floor(total / 60)}m ${String(total % 60).padStart(2, "0")}s`;
};

export default function CreatorVideoEditForm({
  username,
  videoTag,
  onClose,
  onCancel,
  onSuccess,
}) {
  const {
    videoData,
    setVideoData,
    loading,
    error,
    getCreatorVideoEditData,
    updateCreatorVideo,
  } = useCreatorVideos(username);

  const [initializing, setInitializing] = useState(true);
  const [editLoaded, setEditLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const [priceTouched, setPriceTouched] = useState(false);

  const isBusy = loading || initializing || saving;

  const visibility = String(videoData?.visibility || "private").toLowerCase();

  const minimumPrice = getSetPriceAmount(videoData?.videoDuration);

  const priceValidation = validateVideoPriceAmount(
    videoData?.priceAmount,
    videoData?.videoDuration,
  );

  const priceError =
    editLoaded && priceTouched && !priceValidation.valid
      ? priceValidation.message
      : "";

  const canSubmit =
    editLoaded &&
    !isBusy &&
    Boolean(String(videoData?.title || "").trim()) &&
    Boolean(String(videoData?.sport || "").trim()) &&
    VISIBILITY_OPTIONS.some((option) => option.value === visibility) &&
    priceValidation.valid;

  const errorMessage = formError || (error ? getErrorMessage(error, "") : "");

  useEffect(() => {
    let active = true;

    const loadVideo = async () => {
      setInitializing(true);
      setEditLoaded(false);
      setFormError("");
      setPriceTouched(false);

      if (!username || !videoTag) {
        setFormError("Video information is missing.");
        setInitializing(false);
        return;
      }

      try {
        const result = await getCreatorVideoEditData(videoTag);

        if (!active) return;

        if (result === false || result?.success === false || !result?.video) {
          setFormError("Unable to load video details.");
          return;
        }

        setEditLoaded(true);
      } catch (err) {
        if (active) {
          setFormError(getErrorMessage(err, "Unable to load video details."));
        }
      } finally {
        if (active) {
          setInitializing(false);
        }
      }
    };

    void loadVideo();

    return () => {
      active = false;
    };
  }, [username, videoTag]);

  const handleClose = () => {
    if (isBusy) return;

    if (onClose) {
      onClose();
    } else {
      onCancel?.(false);
    }
  };

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape" && !isBusy) {
        event.preventDefault();
        handleClose();
      }
    };

    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isBusy, onClose, onCancel]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;

    setVideoData((previous) => ({
      ...previous,
      [name]: value,
    }));

    setFormError("");
  };

  const handlePriceChange = (event) => {
    const value = typeof event === "string" ? event : event.target.value;

    if (value !== "" && !/^\d*(?:\.\d{0,2})?$/.test(value)) {
      return;
    }

    setVideoData((previous) => ({
      ...previous,
      priceAmount: value,
    }));

    setPriceTouched(true);
    setFormError("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!canSubmit) return;

    const validation = validateVideoPriceAmount(
      videoData.priceAmount,
      videoData.videoDuration,
    );

    if (!validation.valid) {
      setPriceTouched(true);
      setFormError(validation.message);
      return;
    }

    const payload = {
      title: String(videoData.title || "").trim(),
      sport: String(videoData.sport || "").trim(),
      description: String(videoData.description || "").trim(),
      visibility,
      priceAmount: validation.priceAmount,
    };

    setSaving(true);
    setFormError("");

    try {
      const result = await updateCreatorVideo(videoTag, payload);

      if (!result?.success) {
        setFormError(result?.message || "Unable to update video details.");
        return;
      }

      await onSuccess?.(result);

      if (onClose) {
        onClose();
      } else {
        onCancel?.(false);
      }
    } catch (err) {
      console.error("Video update failed:", err);

      setFormError(getErrorMessage(err, "Unable to update video details."));
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <form
      className="creator-video-edit-form"
      onSubmit={handleSubmit}
      aria-label="Edit video"
    >
      <header className="creator-video-edit-form__header">
        <div className="creator-video-edit-form__header-info">
          <h2>Edit Video</h2>
          <p>Update your video information and settings.</p>
        </div>

        <button
          type="button"
          className="creator-video-edit-form__close"
          onClick={handleClose}
          disabled={isBusy}
          aria-label="Close video editor"
        >
          <X size={22} />
        </button>
      </header>

      <div className="creator-video-edit-form__workspace">
        <main className="creator-video-edit-form__main">
          <div className="creator-video-edit-form__section-header">
            <h3>Video Information</h3>
            <p>Update the details displayed to your viewers.</p>
          </div>

          <div className="creator-video-edit-form__fields">
            <TextField
              name="title"
              label="Title"
              value={videoData?.title || ""}
              onChange={handleInputChange}
              placeholder="Match name or highlight title"
              required
              disabled={isBusy || !editLoaded}
            />

            <Select
              name="sport"
              label="Sport"
              value={videoData?.sport || ""}
              onChange={handleInputChange}
              items={SPORT_OPTIONS}
              required
              disabled={isBusy || !editLoaded}
            />

            <TextArea
              name="description"
              label="Description"
              value={videoData?.description || ""}
              onChange={handleInputChange}
              placeholder="Add context for viewers, players or scouts"
              maxLength={500}
              maxHeight={180}
              disabled={isBusy || !editLoaded}
            />
          </div>
        </main>

        <aside className="creator-video-edit-form__sidebar">
          <div className="creator-video-edit-form__section-header">
            <h3>Video Settings</h3>
            <p>Manage visibility and rental pricing.</p>
          </div>

          <div className="creator-video-edit-form__fields">
            <Select
              name="visibility"
              label="Visibility"
              value={visibility}
              onChange={handleInputChange}
              items={VISIBILITY_OPTIONS}
              required
              disabled={isBusy || !editLoaded}
            />
          </div>

          <div className="creator-video-edit-form__pricing">
            <div className="creator-video-edit-form__section-header">
              <h3>Rental Pricing</h3>
              <p>Set the rental price for your video.</p>
            </div>

            <TextField
              name="priceAmount"
              type="number"
              label="Rental Price (AUD)"
              min={minimumPrice ?? undefined}
              step="0.01"
              inputMode="decimal"
              value={videoData?.priceAmount ?? ""}
              onChange={handlePriceChange}
              onBlur={() => setPriceTouched(true)}
              placeholder={
                minimumPrice === null
                  ? "Minimum price unavailable"
                  : `Minimum A$${minimumPrice.toFixed(2)}`
              }
              required
              disabled={isBusy || !editLoaded}
            />

            <p className="creator-video-edit-form__help">
              {minimumPrice === null
                ? "Video duration is required to calculate pricing."
                : `Minimum rental price: A$${minimumPrice.toFixed(2)}.`}
            </p>

            {priceError && (
              <p className="creator-video-edit-form__error">{priceError}</p>
            )}
          </div>

          {editLoaded && (
            <div className="creator-video-edit-form__summary">
              <h4>Video Summary</h4>

              <div>
                <span>Duration</span>
                <strong>
                  {videoData?.videoDuration
                    ? formatDuration(videoData.videoDuration)
                    : "—"}
                </strong>
              </div>

              <div>
                <span>Sport</span>
                <strong>{videoData?.sport || "—"}</strong>
              </div>

              <div>
                <span>Visibility</span>
                <strong>{visibility}</strong>
              </div>
            </div>
          )}
        </aside>
      </div>

      <footer className="creator-video-edit-form__footer">
        <div className="creator-video-edit-form__footer-status">
          {errorMessage ? (
            <span className="creator-video-edit-form__error" role="alert">
              {errorMessage}
            </span>
          ) : initializing ? (
            <span>Loading video details...</span>
          ) : saving ? (
            <span>Saving changes...</span>
          ) : null}
        </div>

        <div className="creator-video-edit-form__actions">
          <Button
            type="button"
            value="Cancel"
            variant="primary-outlined"
            onClick={handleClose}
            disabled={isBusy}
          />

          <Button
            type="submit"
            value={saving ? "Saving..." : "Save Changes"}
            variant="primary"
            disabled={!canSubmit}
          />
        </div>
      </footer>
    </form>,
    document.body,
  );
}
